/**
 * Data analysis thresholds for column detection, chart generation,
 * KPI selection, and data validation heuristics.
 */

/** Thresholds for numeric column and data quality detection */
export const DATA_DETECTION = {
  /** Minimum ratio of numeric values to consider a column numeric (50%) */
  MIN_NUMERIC_RATIO: 0.5,
  /** High numeric ratio — overrides dimension keyword classification (80%) */
  HIGH_NUMERIC_RATIO: 0.8,
  /** Coefficient of variation threshold for "significant variance" (20%) */
  VARIANCE_SIGNIFICANCE: 0.2,
  /** Minimum sample size for meaningful variance analysis */
  MIN_VARIANCE_SAMPLE: 5,
  /** Minimum unique values for categorical chart suitability */
  MIN_CATEGORICAL_VALUES: 2,
  /** Maximum unique values for categorical chart suitability */
  MAX_CATEGORICAL_VALUES: 50,
  /** Default metric type threshold: values > 100 treated as sum, else avg */
  METRIC_SUM_THRESHOLD: 100,
} as const;

/** Chart generation limits */
export const CHART_LIMITS = {
  /** Maximum data points per chart (top N sliced) */
  MAX_DATA_POINTS: 6,
  /** Maximum items for pie chart (above this, use bar chart) */
  PIE_CHART_MAX_ITEMS: 4,
  /** Maximum number of charts returned per analysis */
  MAX_CHARTS: 3,
} as const;

/** KPI dashboard limits */
export const KPI_LIMITS = {
  /** Maximum KPIs displayed on dashboard */
  MAX_KPIS: 6,
  /** Maximum display width for metric names (characters) */
  MAX_METRIC_NAME_WIDTH: 18,
} as const;

/** Data extraction validation thresholds */
export const VALIDATION = {
  /** Small sheet threshold (total cells) — lenient validation below this */
  SMALL_SHEET_CELLS: 50,
  /** Minimum data density (ratio) — below this = too sparse */
  MIN_DATA_DENSITY: 0.1,
  /** Minimum rows for header detection in large sheets */
  MIN_ROWS_LARGE_SHEET: 3,
  /** Size threshold for "large sheet" classification */
  LARGE_SHEET_CELLS: 100,
  /** Minimum rows with data for small sheets */
  MIN_DATA_ROWS_SMALL: 1,
  /** Minimum data rows for large sheets */
  MIN_DATA_ROWS_LARGE: 2,
  /** Minimum rows and columns for tabular data */
  MIN_TABULAR_DIMENSION: 2,
  /** Header row fill ratio threshold */
  MIN_HEADER_FILL_RATIO: 0.3,
  /** Header row text ratio threshold */
  MIN_HEADER_TEXT_RATIO: 0.5,
} as const;

/** Smart Match defaults */
export const SMART_MATCH_DEFAULTS = {
  /** Default matching confidence threshold (%) */
  DEFAULT_THRESHOLD: 75,
} as const;

/** Market Watch filter defaults */
export const MW_FILTER_DEFAULTS = {
  /** Default price variance percentage */
  PRICE_VARIANCE_PCT: 10,
} as const;
